/**
 * Hub Definitions - Extended hubs (Billing, CFO, HR, Teams, AI, Inventory, Assets)
 */

import {
  Briefcase,
  LayoutDashboard,
  BarChart3,
  FileText,
  Receipt,
  Users,
  Settings,
  CheckCircle,
  Clock,
  Bell,
  CalendarOff,
  UserPlus,
  GraduationCap,
  Shield,
  Hammer,
  Truck,
  Bot,
  MessageSquare,
  Code2,
  Link,
  Key,
  Network,
  Package,
  Wrench,
  DollarSign,
  Wand2,
  Zap,
  TrendingUp,
  GitCompare,
  LineChart,
  Target,
  AlertTriangle,
  Inbox,
  FolderTree,
  UserCircle,
  Mail,
  Search,
  Sparkles,
} from 'lucide-react'
import type { HubDefinition } from './types'
import { getHubUrl } from './types'
import type { HubId } from '../../components/types'

export const EXTENDED_HUB_DEFINITIONS: Partial<Record<HubId, HubDefinition>> = {
  billing: {
    id: 'billing', name: 'Billing Hub', shortName: 'Billing',
    description: 'Bill processing, job attribution, and Xero reconciliation',
    icon: DollarSign, baseUrl: getHubUrl('billing'), category: 'manage',
    status: 'development', accentColor: 'emerald', accentGradient: 'from-emerald-500 to-green-600',
    defaultLandingPage: '/reconciliation',
    pages: [
      { id: 'dashboard', label: 'Dashboard', href: '/', icon: LayoutDashboard, description: 'Billing overview' },
      { id: 'reconciliation', label: 'Reconciliation', href: '/reconciliation', icon: GitCompare, description: 'Xero reconciliation', isPrimary: true },
      { id: 'inbox', label: 'Inbox', href: '/inbox', icon: Inbox, description: 'Pending bills' },
      { id: 'bills', label: 'All Bills', href: '/bills', icon: Receipt, description: 'Bill management' },
      { id: 'bill-detail', label: 'Bill', href: '/bills/:id', icon: Receipt, description: 'Bill details' },
      { id: 'approvals', label: 'Approvals', href: '/approvals', icon: CheckCircle, description: 'Approval queue' },
      { id: 'attribution', label: 'Job Attribution', href: '/attribution', icon: Link, description: 'Cost allocation' },
      { id: 'ai-extract', label: 'AI Extract', href: '/extract', icon: Wand2, description: 'Document extraction' },
      { id: 'automation', label: 'Automation Rules', href: '/automation', icon: Zap, description: 'Processing rules' },
      { id: 'supplier-matching', label: 'Supplier Match', href: '/suppliers', icon: Truck, description: 'Supplier matching' },
      { id: 'reports', label: 'Reports', href: '/reports', icon: BarChart3, description: 'Billing reports' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'Billing configuration' },
    ],
  },

  cfo: {
    id: 'cfo', name: 'CFO Hub', shortName: 'CFO',
    description: 'Financial analytics, WIP, cash flow, and executive reporting',
    icon: TrendingUp, baseUrl: getHubUrl('cfo'), category: 'intelligence',
    status: 'development', accentColor: 'purple', accentGradient: 'from-purple-500 to-fuchsia-600',
    defaultLandingPage: '/',
    pages: [
      { id: 'dashboard', label: 'Dashboard', href: '/', icon: LayoutDashboard, description: 'Executive overview', isPrimary: true },
      { id: 'profitability', label: 'Job Profitability', href: '/profitability', icon: TrendingUp, description: 'Profit analysis' },
      { id: 'job-profit', label: 'Job Detail', href: '/profitability/:id', icon: Briefcase, description: 'Job profit breakdown' },
      { id: 'wip', label: 'WIP Analysis', href: '/wip', icon: BarChart3, description: 'Work in progress' },
      { id: 'cashflow', label: 'Cash Flow', href: '/cashflow', icon: DollarSign, description: 'Cash flow analysis' },
      { id: 'governance', label: 'Governance', href: '/governance', icon: GitCompare, description: 'Quotes vs reality — controlling dashboard' },
      { id: 'forecasting', label: 'Forecasting', href: '/forecasting', icon: LineChart, description: 'Revenue forecasting' },
      { id: 'kpi-builder', label: 'KPI Builder', href: '/kpis', icon: Target, description: 'Custom KPI creation' },
      { id: 'executive-reports', label: 'Executive Reports', href: '/reports/executive', icon: FileText, description: 'Board reports' },
      { id: 'alerts', label: 'Financial Alerts', href: '/alerts', icon: Bell, description: 'Financial warnings' },
      { id: 'entity-consolidation', label: 'Consolidation', href: '/consolidation', icon: Network, description: 'Multi-entity view' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'CFO hub settings' },
    ],
  },

  hr: {
    id: 'hr', name: 'HR Hub', shortName: 'HR',
    description: 'Human resources, onboarding, compliance, and training',
    icon: UserPlus, baseUrl: getHubUrl('hr'), category: 'manage',
    status: 'planned', accentColor: 'indigo', accentGradient: 'from-indigo-500 to-violet-600',
    defaultLandingPage: '/employees',
    pages: [
      { id: 'dashboard', label: 'Dashboard', href: '/', icon: LayoutDashboard, description: 'HR overview' },
      { id: 'employees', label: 'Employees', href: '/employees', icon: Users, description: 'Employee directory', isPrimary: true },
      { id: 'employee-detail', label: 'Employee Profile', href: '/employees/:id', icon: UserCircle, description: 'Employee details' },
      { id: 'onboarding', label: 'Onboarding', href: '/onboarding', icon: UserPlus, description: 'New hire process' },
      { id: 'leave', label: 'Leave Management', href: '/leave', icon: CalendarOff, description: 'Leave requests' },
      { id: 'training', label: 'Training', href: '/training', icon: GraduationCap, description: 'Training records' },
      { id: 'compliance', label: 'Compliance', href: '/compliance', icon: Shield, description: 'Compliance tracking' },
      { id: 'documents', label: 'Documents', href: '/documents', icon: FileText, description: 'HR documents' },
      { id: 'expiry-alerts', label: 'Expiry Alerts', href: '/expiry', icon: Bell, description: 'License/cert expiry' },
      { id: 'reports', label: 'Reports', href: '/reports', icon: BarChart3, description: 'HR analytics' },
      { id: 'integrations', label: 'Integrations', href: '/integrations', icon: Link, description: 'Payroll connections' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'HR configuration' },
    ],
  },

  teams: {
    id: 'teams', name: 'Teams Hub', shortName: 'Teams',
    description: 'Team chat, channels, notes, and people directory',
    icon: MessageSquare, baseUrl: getHubUrl('teams'), category: 'manage',
    status: 'production', accentColor: 'violet', accentGradient: 'from-violet-500 to-purple-600',
    defaultLandingPage: '/channels',
    pages: [
      { id: 'dashboard', label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, description: 'Teams overview' },
      { id: 'channels', label: 'Channels', href: '/channels', icon: MessageSquare, description: 'Group discussions', isPrimary: true },
      { id: 'channel-detail', label: 'Channel', href: '/channels/:id', icon: MessageSquare, description: 'Channel messages' },
      { id: 'messages', label: 'Messages', href: '/messages', icon: MessageSquare, description: 'Direct messages' },
      { id: 'notes', label: 'Notes', href: '/notes', icon: FileText, description: 'Cross-hub notes' },
      { id: 'people', label: 'People', href: '/people', icon: Users, description: 'Team directory' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'Teams settings' },
    ],
  },

  developer: {
    id: 'developer', name: 'Developer Hub', shortName: 'Developer',
    description: 'API keys, REST reference, Try It console, and code snippets',
    icon: Code2, baseUrl: getHubUrl('developer'), category: 'intelligence',
    status: 'development', accentColor: 'slate', accentGradient: 'from-slate-600 to-slate-800',
    defaultLandingPage: '/',
    pages: [
      { id: 'home', label: 'Overview', href: '/', icon: LayoutDashboard, description: 'Developer hub home', isPrimary: true },
      { id: 'api-keys', label: 'API Keys', href: '/api-keys', icon: Key, description: 'Create and revoke API keys' },
      { id: 'reference', label: 'API Reference', href: '/reference', icon: FileText, description: 'Endpoints, copy-paste, Try It' },
    ],
  },

  ai: {
    id: 'ai', name: 'AI Hub', shortName: 'AI',
    description: 'AI assistant, automation workflows, and intelligent insights',
    icon: Bot, baseUrl: getHubUrl('ai'), category: 'intelligence',
    status: 'development', accentColor: 'violet', accentGradient: 'from-violet-500 to-purple-600',
    defaultLandingPage: '/chat',
    pages: [
      { id: 'dashboard', label: 'Dashboard', href: '/', icon: LayoutDashboard, description: 'AI overview' },
      { id: 'chat', label: 'AI Chat', href: '/chat', icon: MessageSquare, description: 'Conversational AI', isPrimary: true },
      { id: 'insights', label: 'Insights', href: '/insights', icon: TrendingUp, description: 'AI-generated insights' },
      { id: 'job-insights', label: 'Job Insights', href: '/insights/job/:id', icon: Briefcase, description: 'Job-specific analysis' },
      { id: 'automations', label: 'Automations', href: '/automations', icon: Zap, description: 'Workflow automation' },
      { id: 'automation-detail', label: 'Automation', href: '/automations/:id', icon: Zap, description: 'Automation details' },
      { id: 'predictions', label: 'Predictions', href: '/predictions', icon: LineChart, description: 'Predictive analytics' },
      { id: 'recommendations', label: 'Recommendations', href: '/recommendations', icon: Target, description: 'AI suggestions' },
      { id: 'anomalies', label: 'Anomaly Detection', href: '/anomalies', icon: AlertTriangle, description: 'Unusual patterns' },
      { id: 'training', label: 'Model Training', href: '/training', icon: Bot, description: 'Custom model training' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'AI configuration' },
    ],
  },

  inventory: {
    id: 'inventory', name: 'Inventory Hub', shortName: 'Inventory',
    description: 'Stock tracking, materials management, and reorder alerts',
    icon: Package, baseUrl: getHubUrl('inventory'), category: 'deliver',
    status: 'planned', accentColor: 'orange', accentGradient: 'from-orange-500 to-red-600',
    defaultLandingPage: '/items',
    pages: [
      { id: 'dashboard', label: 'Dashboard', href: '/', icon: LayoutDashboard, description: 'Inventory overview' },
      { id: 'items', label: 'Items', href: '/items', icon: Package, description: 'Current stock levels', isPrimary: true },
      { id: 'item-detail', label: 'Item', href: '/items/:id', icon: Package, description: 'Item details' },
      { id: 'job-allocation', label: 'Job Allocation', href: '/allocation', icon: Briefcase, description: 'Materials by job' },
      { id: 'orders', label: 'Orders', href: '/orders', icon: Receipt, description: 'Purchase orders' },
      { id: 'reorders', label: 'Reorder Alerts', href: '/reorders', icon: Bell, description: 'Low stock alerts' },
      { id: 'suppliers', label: 'Suppliers', href: '/suppliers', icon: Truck, description: 'Supplier management' },
      { id: 'locations', label: 'Locations', href: '/locations', icon: FolderTree, description: 'Warehouse locations' },
      { id: 'costing', label: 'Costing', href: '/costing', icon: DollarSign, description: 'Material costs' },
      { id: 'reports', label: 'Reports', href: '/reports', icon: BarChart3, description: 'Inventory analytics' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'Inventory settings' },
    ],
  },

  assets: {
    id: 'assets', name: 'Assets Hub', shortName: 'Assets',
    description: 'Asset tracking, fleet management, and maintenance scheduling',
    icon: Wrench, baseUrl: getHubUrl('assets'), category: 'deliver',
    status: 'planned', accentColor: 'slate', accentGradient: 'from-slate-500 to-slate-700',
    defaultLandingPage: '/assets',
    pages: [
      { id: 'dashboard', label: 'Dashboard', href: '/', icon: LayoutDashboard, description: 'Asset overview' },
      { id: 'assets', label: 'All Assets', href: '/assets', icon: Wrench, description: 'Asset registry', isPrimary: true },
      { id: 'asset-detail', label: 'Asset', href: '/assets/:id', icon: Wrench, description: 'Asset details' },
      { id: 'fleet', label: 'Fleet', href: '/fleet', icon: Truck, description: 'Vehicle management' },
      { id: 'vehicle-detail', label: 'Vehicle', href: '/fleet/:id', icon: Truck, description: 'Vehicle details' },
      { id: 'tools', label: 'Tools', href: '/tools', icon: Hammer, description: 'Tool inventory' },
      { id: 'allocation', label: 'Allocation', href: '/allocation', icon: Briefcase, description: 'Job allocation' },
      { id: 'maintenance', label: 'Maintenance', href: '/maintenance', icon: Settings, description: 'Service scheduling' },
      { id: 'utilization', label: 'Utilization', href: '/utilization', icon: BarChart3, description: 'Usage analytics' },
      { id: 'depreciation', label: 'Depreciation', href: '/depreciation', icon: TrendingUp, description: 'Value tracking' },
      { id: 'reports', label: 'Reports', href: '/reports', icon: BarChart3, description: 'Asset reports' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'Asset settings' },
    ],
  },

  governance: {
    // Display label is "Compliance" to distinguish from the CFO "Governance"
    // controlling dashboard (/governance/*). The registry id stays 'governance'
    // (keyed by HubId, accessControl + designTokens) to avoid a wider rename.
    id: 'governance', name: 'Compliance Hub', shortName: 'Compliance',
    description: 'ISO master-data compliance — currency, validation, monthly sign-off',
    icon: Shield, baseUrl: getHubUrl('governance'), category: 'manage',
    status: 'development', accentColor: 'indigo', accentGradient: 'from-indigo-500 to-violet-600',
    defaultLandingPage: '/',
    pages: [
      { id: 'cockpit', label: 'Cockpit', href: '/', icon: LayoutDashboard, description: 'Compliance score + sign-off', isPrimary: true },
      { id: 'suppliers', label: 'Supplier Price Lists', href: '/domains/supplier_price_lists', icon: Truck, description: 'Price-list currency' },
      { id: 'employees', label: 'Employee Details', href: '/domains/employee_details', icon: Users, description: 'Employee master data' },
      { id: 'review', label: 'Monthly Review', href: '/review', icon: CheckCircle, description: 'Attest the period' },
      { id: 'audit', label: 'Audit Trail', href: '/audit', icon: FileText, description: 'Validations + sign-offs' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'Domain configuration' },
    ],
  },

  smartdocs: {
    id: 'smartdocs', name: 'SmartDocs Hub', shortName: 'SmartDocs',
    description: 'Provenance + access spine over a job\'s documents — cited assistant, revisions, audit dossier',
    icon: Sparkles, baseUrl: getHubUrl('smartdocs'), category: 'intelligence',
    status: 'production', accentColor: 'orange', accentGradient: 'from-orange-500 to-rose-600',
    defaultLandingPage: '/home',
    pages: [
      { id: 'home', label: 'Home', href: '/home', icon: LayoutDashboard, description: 'SmartDocs overview', isPrimary: true },
      { id: 'assistant', label: 'Assistant', href: '/assistant', icon: MessageSquare, description: 'Cited AI assistant' },
      { id: 'jobs', label: 'Jobs', href: '/jobs', icon: Briefcase, description: 'Job document spine' },
      { id: 'documents', label: 'Documents', href: '/exchange', icon: FolderTree, description: 'Versions & revisions' },
      { id: 'inbox', label: 'Inbox', href: '/exchange/inbox', icon: Inbox, description: 'Email → version' },
      { id: 'workflows', label: 'Workflows', href: '/workflows', icon: Zap, description: 'Draft studio' },
      { id: 'audit', label: 'Audit Dossier', href: '/audit', icon: FileText, description: 'Provenance dossier' },
      { id: 'search', label: 'Search', href: '/search', icon: Search, description: 'Cited, access-scoped search' },
      { id: 'integrations', label: 'Integrations', href: '/integrations', icon: Link, description: 'Connector status' },
      { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, description: 'SmartDocs preferences' },
    ],
  },
}
